"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { FiX, FiCheck, FiArrowDown, FiChevronDown } from "react-icons/fi";
import { useUnifiedBalance } from "@/contexts/UnifiedBalanceContext";
import { useBridge, BridgeDirection } from "@/hooks/useBridge";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useT } from "@/hooks/useT";
import { STATIC_ASSETS } from "@/lib/cdn";
import { celebrateBurst } from "@/lib/confetti";
import { usePredictEnabled } from "@/hooks/usePredictEnabled";

// Markets — user-facing names (never show chain/token names).
// Predict.fun: re-inserted gated by ENABLE_PREDICT. ⚠️ Before flipping the flag,
// verify useBridge actually routes to/from predictfun (BSC/USDT) — the venue
// option is filtered out of the dropdowns while the flag is off (see VISIBLE_MARKETS).
// "Arbitrum" is exposed as the bridge waypoint for Hyperliquid: HL bridge2
// settles in USDC on Arbitrum, so users with funds withdrawn from HL (or
// who plan to manually deposit to HL) route through this venue. Phase 2
// will collapse this into a first-class "Hyperliquid" option that
// automates the bridge2 deposit/withdraw3 steps.
const MARKETS = [
  { id: "polymarket", label: "Polymarket", icon: STATIC_ASSETS.polymarket.iconBlue },
  { id: "kalshi", label: "Kalshi", icon: "/logos/kalshi/k-icon.svg" },
  { id: "arbitrum", label: "Hyperliquid (Arbitrum)", icon: "/logos/hyperliquid/logo-green-white.svg" },
  { id: "predictfun", label: "Predict.fun", icon: STATIC_ASSETS.predict.iconBP },
] as const;

type MarketId = (typeof MARKETS)[number]["id"];

// Predict.fun is filtered out of the venue dropdowns unless the user has Predict
// access (per-user allowlist via usePredictEnabled) — computed in-component below.

function directionFromMarkets(from: MarketId, to: MarketId | null): BridgeDirection | null {
  if (!to) return null;
  const chainMap: Record<string, string> = {
    polymarket: "polygon",
    predictfun: "bsc",
    kalshi: "solana",
    arbitrum: "arbitrum",
  };
  const fromChain = chainMap[from];
  const toChain = chainMap[to];
  if (!fromChain || !toChain || fromChain === toChain) return null;
  return `${fromChain}_to_${toChain}` as BridgeDirection;
}

/** Custom dropdown for market selection — icon + label, clean look. */
function MarketDropdown({
  value,
  onChange,
  options,
  placeholder = "Select",
}: {
  value: MarketId | null;
  onChange: (id: MarketId) => void;
  options: readonly { id: MarketId; label: string; icon: string }[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = value ? options.find((o) => o.id === value) : null;

  return (
    <div ref={containerRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-sm font-medium text-gray-900 dark:text-white outline-none"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {selected ? (
            <>
              <img src={selected.icon} alt="" className="w-6 h-6 rounded-[5px] flex-shrink-0" />
              <span className="truncate">{selected.label}</span>
            </>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
          )}
        </div>
        <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-20 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] shadow-lg overflow-hidden">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onChange(o.id); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors ${
                value === o.id ? "bg-gray-50 dark:bg-[#0f0f12]" : ""
              }`}
            >
              <img src={o.icon} alt="" className="w-6 h-6 rounded-[5px] flex-shrink-0" />
              <span className="flex-1 truncate text-gray-900 dark:text-white">{o.label}</span>
              {value === o.id && <FiCheck className="w-4 h-4 text-emerald-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatUsd(n: number | null): string {
  if (n === null) return "$0.00";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

interface MoveFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialFrom?: MarketId;
}

type Step = "input" | "progress" | "complete" | "error";

// Bridge progress stages
const PROGRESS_STAGES = [
  { key: "signing", label: "Signing transaction", emoji: "🔐" },
  { key: "approved", label: "Approval confirmed", emoji: "✅" },
  { key: "bridging", label: "Bridging cross-chain", emoji: "🚀" },
  { key: "completed", label: "Funds delivered", emoji: "🎉" },
];

export default function MoveFundsModal({ isOpen, onClose, initialFrom }: MoveFundsModalProps) {
  useBodyScrollLock(isOpen);
  const t = useT();
  const predictEnabled = usePredictEnabled();
  const VISIBLE_MARKETS = MARKETS.filter((m) => m.id !== "predictfun" || predictEnabled);

  const { polymarket, predictfun, kalshi, arbitrum, hyperliquid, refetch: refetchBalance } = useUnifiedBalance();
  const { quote, getQuote, execute, bridgeStatus, isQuoting, isExecuting, error, reset } = useBridge();

  const [from, setFrom] = useState<MarketId>(initialFrom || "polymarket");
  const [to, setTo] = useState<MarketId | null>(null);
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState<Step>("input");
  const [progressPct, setProgressPct] = useState(0);
  const quoteTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get balance for selected source
  // `predictfun` retained as a fallback in `directionFromMarkets` and
  // chain map even though MARKETS no longer offers it as an option, so
  // the type-narrowed branch below stays exhaustive across the union.
  const sourceBalance =
    from === "polymarket" ? polymarket.usdc :
    (from as string) === "predictfun" ? predictfun.usdt :
    from === "arbitrum" ? arbitrum.usdc :
    kalshi.usdc;
  const amountNum = parseFloat(amount) || 0;
  const isValidAmount = amountNum >= 5 && amountNum <= 10000 && amountNum <= (sourceBalance || 0);

  // HL HyperCore balance (USDC + USDH inside the trading account, not yet
  // withdrawn to Arbitrum). Useful when from === "arbitrum" so the user
  // can see they have HL funds even though the bridge can't move them
  // until they withdraw3 first.
  //
  // hyperliquid.usdc = HL HyperCore + Arbitrum EOA (effective)
  // arbitrum.usdc    = Arbitrum EOA only (bridgeable)
  // → HC = hyperliquid - arbitrum
  const hcOnlyBalance = (hyperliquid.usdc != null && arbitrum.usdc != null)
    ? Math.max(0, hyperliquid.usdc - arbitrum.usdc)
    : null;
  const hasHlFundsToWithdraw = from === "arbitrum"
    && hcOnlyBalance != null
    && hcOnlyBalance > 0.5; // ignore dust

  // Direction
  const direction = directionFromMarkets(from, to);

  // Auto-fetch quote when amount changes
  useEffect(() => {
    if (!isOpen || !direction || !isValidAmount) return;
    if (quoteTimerRef.current) clearInterval(quoteTimerRef.current);

    const fetchQuote = () => getQuote(amountNum, direction);
    const debounce = setTimeout(fetchQuote, 500);
    quoteTimerRef.current = setInterval(fetchQuote, 15000);

    return () => {
      clearTimeout(debounce);
      if (quoteTimerRef.current) clearInterval(quoteTimerRef.current);
    };
  }, [isOpen, amountNum, direction, isValidAmount, getQuote]);

  // Animate progress bar
  useEffect(() => {
    if (step !== "progress") {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      return;
    }

    // Determine target based on bridge status
    let target = 15; // signing
    if (bridgeStatus?.status === "approved") target = 45;
    if (bridgeStatus?.status === "bridging") target = 75;
    if (bridgeStatus?.status === "completed") target = 100;

    progressTimerRef.current = setInterval(() => {
      setProgressPct(prev => {
        if (prev >= target) return prev;
        return prev + (target - prev) * 0.1; // Ease toward target
      });
    }, 100);

    return () => { if (progressTimerRef.current) clearInterval(progressTimerRef.current); };
  }, [step, bridgeStatus?.status]);

  // Track bridge status changes
  useEffect(() => {
    if (!bridgeStatus) return;
    if (bridgeStatus.status === "completed") {
      setProgressPct(100);
      setTimeout(() => {
        setStep("complete");
        refetchBalance();
        celebrateBurst();
      }, 600);
    } else if (bridgeStatus.status === "failed") {
      setStep("error");
    }
  }, [bridgeStatus, refetchBalance]);

  // Swap from/to — no-op when destination isn't picked yet.
  const handleSwap = useCallback(() => {
    if (!to) return;
    setFrom(to);
    setTo(from);
  }, [from, to]);

  // Execute bridge
  const handleExecute = useCallback(async () => {
    if (!direction || !isValidAmount) return;
    setStep("progress");
    setProgressPct(0);
    const result = await execute(amountNum, direction);
    if (!result) setStep("error");
  }, [direction, isValidAmount, amountNum, execute]);

  // Max button
  const handleMax = useCallback(() => {
    if (sourceBalance !== null) {
      const max = Math.min(sourceBalance, 10000);
      setAmount(max.toFixed(2));
    }
  }, [sourceBalance]);

  // Reset on close
  const handleClose = useCallback(() => {
    if (quoteTimerRef.current) clearInterval(quoteTimerRef.current);
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    reset();
    setStep("input");
    setAmount("");
    setProgressPct(0);
    onClose();
  }, [onClose, reset]);

  if (!isOpen) return null;

  // Current progress stage index
  const stageIndex = !bridgeStatus ? 0
    : bridgeStatus.status === "approved" ? 1
    : bridgeStatus.status === "bridging" ? 2
    : bridgeStatus.status === "completed" ? 3
    : 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] shadow-xl overflow-hidden">

        {/* Progress bar at top — visible during progress */}
        {step === "progress" && (
          <div className="h-1 bg-gray-100 dark:bg-[#0a0a0b]">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 transition-all duration-300 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {step === "complete" ? t("bridge.modal.titleComplete") : step === "progress" ? t("bridge.modal.titleProgress") : t("bridge.modal.title")}
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {/* ── INPUT STATE ────────────────────────────────────── */}
          {step === "input" && (
            <>
              {/* From */}
              <div className="mb-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">{t("bridge.modal.from")}</label>
                <div className="flex items-center justify-between p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A]">
                  <MarketDropdown
                    value={from}
                    onChange={(newFrom) => { setFrom(newFrom); if (to === newFrom) setTo(null); }}
                    options={VISIBLE_MARKETS}
                    placeholder={t("bridge.modal.selectPlaceholder")}
                  />
                  <div className="text-right ml-3 flex-shrink-0">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{formatUsd(sourceBalance)}</span>
                    {/* HyperCore breakdown so users see why "Hyperliquid (Arbitrum)"
                        reads $0 while their HL trading account has funds — Bridge2
                        only moves USDC sitting in the Arbitrum EOA, not USDH/USDC
                        inside HyperCore. */}
                    {hasHlFundsToWithdraw && (
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                        HL Account: {formatUsd(hcOnlyBalance)}
                      </div>
                    )}
                  </div>
                </div>
                {/* Explanatory banner when the user has HL funds that aren't
                    bridgeable yet. Pops the global HyperliquidWithdrawModal
                    in-place via the `frenflow:hl-withdraw-open` window event
                    so the user never leaves the bridge surface — they
                    withdraw3, wait 3-5 min, come back here once the Arbitrum
                    EOA hook refetches with the new balance. */}
                {hasHlFundsToWithdraw && (sourceBalance ?? 0) < 5 && (
                  <div className="mt-2 p-2.5 rounded-[5px] bg-amber-50 dark:bg-amber-500/[0.06] border border-amber-200 dark:border-amber-500/20 text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
                    You have <strong>{formatUsd(hcOnlyBalance)}</strong> inside your Hyperliquid account.
                    The bridge moves USDC sitting on Arbitrum — withdraw from HL first.{" "}
                    <button
                      type="button"
                      className="underline font-semibold text-amber-900 dark:text-amber-200"
                      onClick={() => {
                        // Close Move Funds first so the two modals don't
                        // stack; AppLayoutWithSidebar mounts a single global
                        // HyperliquidWithdrawModal that listens for this event.
                        onClose();
                        if (typeof window !== "undefined") {
                          window.dispatchEvent(new CustomEvent("frenflow:hl-withdraw-open"));
                        }
                      }}
                    >
                      Withdraw from HL →
                    </button>
                  </div>
                )}
              </div>

              {/* Swap button */}
              <div className="flex justify-center -my-1.5 relative z-10">
                <button
                  onClick={handleSwap}
                  className="p-2 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:rotate-180 transition-all duration-300"
                >
                  <FiArrowDown className="w-4 h-4" />
                </button>
              </div>

              {/* To */}
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">{t("bridge.modal.to")}</label>
                <div className="flex items-center p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A]">
                  <MarketDropdown
                    value={to}
                    onChange={setTo}
                    options={VISIBLE_MARKETS.filter((m) => m.id !== from)}
                    placeholder={t("bridge.modal.selectDestination")}
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">{t("bridge.modal.amount")}</label>
                <div className="flex items-center gap-2 p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] focus-within:border-gray-400 dark:focus-within:border-gray-600 transition-colors">
                  <span className="text-gray-400 text-lg">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    min="5"
                    max="10000"
                    step="0.01"
                    className="flex-1 bg-transparent text-lg font-semibold text-gray-900 dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    onClick={handleMax}
                    className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2.5 py-1 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#25252B] transition-colors"
                  >
                    {t("bridge.modal.max")}
                  </button>
                </div>
                {amountNum > 0 && amountNum < 5 && (
                  <p className="text-xs text-red-500 mt-1">{t("bridge.modal.minAmount")}</p>
                )}
                {amountNum > (sourceBalance || 0) && (
                  <p className="text-xs text-red-500 mt-1">{t("bridge.modal.insufficient")}</p>
                )}
              </div>

              {/* Quote summary */}
              {isValidAmount && (
                <div className="mb-4 p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] space-y-2">
                  {isQuoting ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                      {t("bridge.modal.gettingRoute")}
                    </div>
                  ) : quote ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">{t("bridge.modal.youReceive")}</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">{formatUsd(quote.estimatedReceive)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">{t("bridge.modal.fee")}</span>
                        <span className="text-gray-400">{formatUsd(quote.estimatedFee)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">{t("bridge.modal.estimatedTime")}</span>
                        <span className="text-gray-400">{t("bridge.modal.minutes", { n: quote.estimatedTimeMinutes })}</span>
                      </div>
                    </>
                  ) : null}
                </div>
              )}

              {/* Execute button.
                  isExecuting in the disabled clause prevents a double-click
                  race: setStep("progress") is queued by handleExecute and the
                  button only un-renders on the next React tick, so a fast
                  double tap could otherwise fire execute() twice and submit
                  TWO bridge txs. */}
              <button
                onClick={handleExecute}
                disabled={!to || !isValidAmount || !quote || isQuoting || isExecuting}
                className="w-full py-3.5 rounded-[5px] text-sm font-bold
                  bg-gray-900 dark:bg-white text-white dark:text-gray-900
                  hover:bg-gray-800 dark:hover:bg-gray-100
                  disabled:opacity-30 disabled:cursor-not-allowed
                  transition-colors"
              >
                {!to ? t("bridge.modal.selectDestination") : t("bridge.modal.title")}
              </button>

              {error && (
                <p className="text-xs text-red-500 mt-2 text-center">{error}</p>
              )}
            </>
          )}

          {/* ── PROGRESS STATE ────────────────────────────────── */}
          {step === "progress" && (
            <div className="py-4 space-y-4">
              {/* Route info — always visible */}
              <div className="flex items-center justify-between p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A]">
                <div className="flex items-center gap-2">
                  <img src={MARKETS.find(m => m.id === from)?.icon} alt="" className="w-5 h-5 rounded-[5px]" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{MARKETS.find(m => m.id === from)?.label}</span>
                </div>
                <span className="text-xs text-gray-400">→</span>
                <div className="flex items-center gap-2">
                  <img src={MARKETS.find(m => m.id === to)?.icon} alt="" className="w-5 h-5 rounded-[5px]" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{MARKETS.find(m => m.id === to)?.label}</span>
                </div>
              </div>

              {/* Amount + quote info */}
              <div className="p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("bridge.modal.amount")}</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formatUsd(amountNum)}</span>
                </div>
                {quote && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t("bridge.modal.youReceive")}</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">{formatUsd(quote.estimatedReceive)}</span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center gap-3 p-3 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300 rounded-full animate-spin flex-shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {bridgeStatus?.status === 'bridging' ? t("bridge.modal.bridging") :
                   bridgeStatus?.status === 'approved' ? t("bridge.modal.approved") :
                   t("bridge.modal.signing")}
                </span>
              </div>

              <p className="text-xs text-gray-400 text-center">
                {t("bridge.modal.canClose")}
              </p>
            </div>
          )}

          {/* ── COMPLETE STATE ────────────────────────────────── */}
          {step === "complete" && (
            <div className="py-6 space-y-4">
              <div className="p-4 rounded-[5px] bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatUsd(bridgeStatus?.amount_to || amountNum)}
                </p>
                <p className="text-sm text-green-600/70 dark:text-green-400/70 mt-1">
                  {t("bridge.modal.addedTo", { market: MARKETS.find(m => m.id === to)?.label || "" })}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3.5 rounded-[5px] text-sm font-bold bg-green-600 hover:bg-green-500 text-white transition-colors"
              >
                {t("bridge.modal.done")}
              </button>
            </div>
          )}

          {/* ── ERROR STATE ───────────────────────────────────── */}
          {step === "error" && (
            <div className="py-6 space-y-4">
              <div className="p-4 rounded-[5px] bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">{t("bridge.modal.transferFailed")}</p>
                <p className="text-xs text-red-500/70 dark:text-red-400/70 mt-1">
                  {bridgeStatus?.error_message || error || t("bridge.modal.somethingWrong")}
                </p>
              </div>
              <button
                onClick={() => { setStep("input"); setProgressPct(0); reset(); }}
                className="w-full py-3 rounded-[5px] text-sm font-semibold bg-gray-900 dark:bg-white text-white dark:text-gray-900 transition-colors"
              >
                {t("bridge.modal.tryAgain")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
